import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useItemTooltip, useSvgRef, useXAxis, useXScale, useYAxis, useYScale } from '@mui/x-charts';
import { BarTooltipProps } from './types';

export const BarTooltip = ({ orientation = 'vertical', series, data }: BarTooltipProps) => {
    const theme = useTheme();
    const tooltipData = useItemTooltip<'bar'>();
    const xAxisInfo = useXAxis();
    const yAxisInfo = useYAxis();
    const xScale = useXScale();
    const yScale = useYScale();
    const svgRef = useSvgRef();

    if (
        !tooltipData ||
        tooltipData.identifier.type !== 'bar' ||
        tooltipData.identifier.dataIndex === undefined ||
        tooltipData.identifier.seriesId === undefined ||
        tooltipData.value === null ||
        tooltipData.formattedValue === undefined ||
        !svgRef.current
    ) {
        return null;
    }

    const { dataIndex } = tooltipData.identifier;
    const seriesLabelFromTooltip = tooltipData.label;

    const itemLabel = orientation === 'vertical' ? xAxisInfo.data?.[dataIndex] : yAxisInfo.data?.[dataIndex];
    const displayValue = tooltipData.formattedValue;

    if (itemLabel === undefined) {
        return null;
    }

    let svgXPosition: number;
    let svgYPosition: number;
    let xOffset: number = 0;
    const yOffset: number = 0;
    const placement = 'top';
    const popperOffset: [number, number] = [0, 10];

    const barSize =
        orientation === 'vertical'
            ? 'bandwidth' in xScale && typeof xScale.bandwidth === 'function'
                ? xScale.bandwidth()
                : 0
            : 'bandwidth' in yScale && typeof yScale.bandwidth === 'function'
            ? yScale.bandwidth()
            : 0;

    if (orientation === 'vertical') {
        svgXPosition = (xScale as (val: string | number | Date) => number)(itemLabel as string) ?? 0;
        svgYPosition = yScale(tooltipData.value) ?? 0;
        xOffset = barSize / 2;
    } else {
        let cumulativeValueBefore = 0;
        const currentSegmentValue = tooltipData.value;
        let currentSeriesObject: NonNullable<BarTooltipProps['series']>[number] | undefined = undefined;
        let currentSeriesOriginalIndex = -1;

        if (dataIndex !== undefined && series && tooltipData.identifier.seriesId !== undefined) {
            for (let i = 0; i < series.length; i++) {
                const seriesItem = series[i];
                if (seriesItem && seriesItem.label === seriesLabelFromTooltip) {
                    currentSeriesObject = seriesItem;
                    currentSeriesOriginalIndex = i;
                    break;
                }
            }

            if (currentSeriesObject) {
                const currentStack = currentSeriesObject.stack || undefined;

                for (let i = 0; i < currentSeriesOriginalIndex; i++) {
                    const priorSeries = series[i];
                    if (priorSeries) {
                        const priorStack = priorSeries.stack || undefined;
                        const value = priorSeries.data?.[dataIndex] ?? 0;

                        if (priorStack === currentStack) {
                            if (typeof value === 'number') {
                                cumulativeValueBefore += value;
                            }
                        }
                    }
                }
            }
        }

        const barSegmentStartScreenX = xScale(cumulativeValueBefore) ?? 0;
        const barSegmentEndScreenX = xScale(cumulativeValueBefore + currentSegmentValue) ?? 0;
        svgXPosition = (barSegmentStartScreenX + barSegmentEndScreenX) / 2;

        svgYPosition = (yScale as (val: string | number | Date) => number)(itemLabel as string) ?? 0;
    }

    const svgRect = svgRef.current.getBoundingClientRect();
    const tooltipPosition = {
        x: svgRect.left + svgXPosition + xOffset,
        y: svgRect.top + svgYPosition + yOffset,
    };

    return (
        <Popper
            sx={{
                pointerEvents: 'none',
                zIndex: (theme) => theme.zIndex.modal + 1,
            }}
            open
            placement={placement}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: popperOffset,
                    },
                },
            ]}
            anchorEl={{
                getBoundingClientRect: () => ({
                    x: tooltipPosition.x,
                    y: tooltipPosition.y,
                    top: tooltipPosition.y,
                    left: tooltipPosition.x,
                    right: tooltipPosition.x,
                    bottom: tooltipPosition.y,
                    width: 0,
                    height: 0,
                    toJSON: () => '',
                }),
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: (theme) => theme.palette.text.primary,
                    color: (theme) => theme.palette.common.white,
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: 0,
                        height: 0,
                        ...(orientation === 'vertical'
                            ? {
                                  borderLeft: '8px solid transparent',
                                  borderRight: '8px solid transparent',
                                  borderTop: `8px solid ${theme.palette.text.primary}`,
                                  bottom: '-8px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                              }
                            : {
                                  borderLeft: '8px solid transparent',
                                  borderRight: '8px solid transparent',
                                  borderTop: `8px solid ${theme.palette.text.primary}`,
                                  bottom: '-8px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                              }),
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.3 }}>
                        {String(itemLabel)}
                    </Typography>

                    {seriesLabelFromTooltip ? (
                        <Typography variant="body2" fontWeight="bold" sx={{ color: 'inherit', lineHeight: 1.3 }}>
                            <Box component="span" sx={{ fontWeight: 'normal' }}>{`${seriesLabelFromTooltip}: `}</Box>
                            {displayValue}
                        </Typography>
                    ) : (
                        <Typography variant="body2" fontWeight="bold" sx={{ color: 'inherit', lineHeight: 1.3 }}>
                            {displayValue}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Popper>
    );
};
